import { useStore } from '@tanstack/react-form'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { SessionCreateResponse } from 'types'
import { z } from 'zod'

import { useAppForm } from '@hooks/form'
import useErrorMessage from '@hooks/use-error-message'

import { Button } from '@shadcn/button'

import { useMutationSessionCreate } from '@api/session/session.mutations'

import FieldErrors from '@/components/form/field-errors'

export const Route = createFileRoute('/session/login/')({
  component: RouteComponent,
})

const loginSchema = z.object({
  email: z.string().min(1, { message: 'form.error.mandatory' }),
  password: z.string().min(1, { message: 'form.error.mandatory' }),
})

type LoginFormSchema = z.infer<typeof loginSchema>

const defaultValues = {
  email: '',
  password: '',
} as LoginFormSchema

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const errorMessage = useErrorMessage()

  const { mutate, isPending } = useMutationSessionCreate()

  const onReceiveResponse = (response: SessionCreateResponse) => {
    if ('twoFactorRequired' in response)
      navigate({
        to: '/session/login/2fa',
        search: { maskedEmail: response.maskedEmail },
      })
    else navigate({ to: '/app' })
  }

  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: loginSchema },
    onSubmit: ({ value, formApi }) => {
      mutate(value, {
        onSuccess: (data) => onReceiveResponse(data),
        onError: (error) => {
          const message = errorMessage({
            error,
            messageByText: {
              'Invalid user credentials': t(
                'session.create.error.invalid-credentials',
              ),
            },
          })

          formApi.setErrorMap({
            onSubmit: {
              form: message,
              fields: {},
            },
          })
        },
      })
    },
  })

  const errors = useStore(form.store, (state) =>
    state.errors.filter((e) => typeof e === 'string'),
  )

  return (
    <div className="flex w-full max-w-82 flex-col gap-6 rounded-md border bg-white p-4 shadow-lg">
      <h1 className="text-lg font-bold">{t('session.create.title')}</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
        className="flex flex-col gap-4"
      >
        <form.AppField name="email">
          {(field) => (
            <field.TextField
              label={t('session.create.email.label')}
              placeholder={t('session.create.email.placeholder')}
            />
          )}
        </form.AppField>
        <form.AppField name="password">
          {(field) => (
            <field.TextField
              label={t('session.create.password.label')}
              placeholder={t('session.create.password.placeholder')}
              type="password"
            />
          )}
        </form.AppField>
        {!!errors.length && <FieldErrors errors={errors} />}
        <Route.Link to="/session/password-reset">
          <Button
            size="ghost"
            variant="ghostPrimary"
            className="w-full"
            type="button"
          >
            {t('session.create.forgotten-password')}
          </Button>
        </Route.Link>
        <form.AppForm>
          <form.SubmitButton pending={isPending} variant="primary" size="sm">
            <div className="flex items-center gap-1">
              <span>{t('session.create.submit')}</span>
              <ArrowRight className="size-4 rounded-md" />
            </div>
          </form.SubmitButton>
        </form.AppForm>
      </form>
    </div>
  )
}
